import { type PropsWithChildren, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import TenantAccessDeniedScreen from '../screens/TenantAccessDenied/TenantAccessDeniedScreen';
import { LastLocation } from '../types';
import { getTenantFromPath } from '../utils/tenant-utils';
import { useAuthContext } from './AuthContext';
import { useMembership } from './MembershipContext';

export const SigningIn = ({ label = 'Signing you in…' }: { label?: string }) => (
  <p className="govuk-body" role="status" aria-live="polite">
    {label}
  </p>
);

export const RequireAuth = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, signIn } = useAuthContext();
  const { currentTenantAllowed } = useMembership();
  const redirectStarted = useRef(false);

  // Extract a valid tenant from the current URL path
  const urlTenant = getTenantFromPath(location.pathname);
  const returnTo = `${location.pathname}${location.search || ''}`;

  useEffect(() => {
    if (isAuthenticated || isLoading || !urlTenant || redirectStarted.current) return;
    redirectStarted.current = true;
    void signIn(returnTo);
  }, [isAuthenticated, isLoading, returnTo, signIn, urlTenant]);

  if (!urlTenant) {
    return <Navigate to="/error" replace />;
  }

  if (!isAuthenticated) {
    return <SigningIn />;
  }

  // Preserve query string for flows that rely on URL params (for example email prefill).
  sessionStorage.setItem(LastLocation, returnTo);

  if (currentTenantAllowed === false) {
    return <TenantAccessDeniedScreen />;
  }
  return children;
};
